"use client";
import { memo } from "react";
import { useTranslations } from "next-intl";
import { Monitor, Maximize, ZoomIn, ZoomOut } from "lucide-react";

interface ScreenShareBannerProps {
  presenterName: string;
  isLocal: boolean;
  isSharing: boolean;
  participantCount?: number;
  onStopScreenShare?: () => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onFullScreen?: () => void;
  zoomLevel?: number;
}

export const ScreenShareBanner = memo(
  ({
    presenterName,
    isLocal,
    isSharing,
    onStopScreenShare,
    onZoomIn,
    onZoomOut,
    onFullScreen,
    zoomLevel = 1
  }: ScreenShareBannerProps) => {
    const t = useTranslations();
    if (!isSharing) return null;

    return (
      <div className="shrink-0 mb-1.5 sm:mb-3 px-1 sm:px-2 transition-all duration-300 ease-in-out animate-in slide-in-from-top-2 fade-in overflow-visible">
        <div className="w-full max-w-4xl mx-auto bg-[#303134] border border-[#48494c] rounded-lg shadow-lg flex items-center justify-between py-1 px-2 sm:px-3 gap-2 sm:gap-4 overflow-visible">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-primary/10 hidden sm:flex items-center justify-center">
              <Monitor className="w-3 h-3 sm:w-4 sm:h-4 text-primary-light" />
            </div>

            <div className="flex flex-col min-w-0">
              <span className="text-white font-medium text-xs sm:text-sm truncate">
                {isLocal ? t("meeting.presenting") : t("meeting.presentingUser", { name: presenterName })}
              </span>
              {isLocal && (
                <span className="text-green-400 text-[10px] sm:text-xs hidden sm:inline-block">
                  {t("meeting.screenVisible")}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0 overflow-visible">
            {isLocal && onStopScreenShare && (
              <button
                onClick={onStopScreenShare}
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1 sm:py-1.5 bg-primary hover:bg-primary-hover text-white text-xs sm:text-sm font-medium rounded-full transition-colors shadow-sm whitespace-nowrap"
              >
                <span className="hidden sm:inline">{t("meeting.stopPresenting")}</span>
                <span className="sm:hidden">{t("meeting.stop")}</span>
              </button>
            )}

            {!isLocal && (
              <div className="flex items-center gap-2 relative overflow-visible">
                {/* Show zoom percentage only when zoomed in (above 100%) */}
                {zoomLevel > 1 && (
                  <div className="bg-gray-700 text-white text-sm font-semibold px-3 py-1 rounded-lg whitespace-nowrap">
                    {Math.round(zoomLevel * 100)}%
                  </div>
                )}

                {/* Zoom In button - always visible */}
                <button
                  onClick={onZoomIn}
                  disabled={zoomLevel >= 2}
                  className={`p-2 rounded-full transition-colors ${
                    zoomLevel >= 2
                      ? "text-gray-600 cursor-not-allowed"
                      : "hover:bg-white/10 text-gray-300 hover:text-white"
                  }`}
                  title={t("meeting.zoomIn")}
                  aria-label={t("meeting.zoomIn")}
                >
                  <ZoomIn className="w-5 h-5" aria-hidden="true" />
                </button>

                {/* Zoom Out button - always visible, disabled at 100% */}
                <button
                  onClick={onZoomOut}
                  disabled={zoomLevel <= 1}
                  className={`p-2 rounded-full transition-colors ${
                    zoomLevel <= 1
                      ? "text-gray-600 cursor-not-allowed"
                      : "hover:bg-white/10 text-gray-300 hover:text-white"
                  }`}
                  title={t("meeting.zoomOut")}
                  aria-label={t("meeting.zoomOut")}
                >
                  <ZoomOut className="w-5 h-5" aria-hidden="true" />
                </button>

                <div className="w-px h-4 bg-gray-600 mx-1" />
              </div>
            )}

            {/* Fullscreen button - available for both presenter and viewers */}
            <button
              onClick={onFullScreen}
              className="p-2 rounded-full hover:bg-white/10 text-gray-300 hover:text-white transition-colors"
              title={t("meeting.fullScreen")}
              aria-label={t("meeting.fullScreen")}
            >
              <Maximize className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    );
  }
);

ScreenShareBanner.displayName = "ScreenShareBanner";
